/**
 * PlayerProfilePage
 *
 * Two-tab layout:
 *   Profile — XP from gamification leaderboard, domain cards, characters
 *   Settings — display name edit, review_reminder radio, email toggles
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { participantAPI, gamificationAPI, emailAPI, authAPI, characterAPI, adaptiveAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CharacterPickerModal from '../components/CharacterPickerModal';
import LearnerNav from '../components/nav/LearnerNav';

// ─── Sub-components ────────────────────────────────────────────────────────

function AchievementBadge({ achievement }) {
  return (
    <div className="ppp-achievement" title={achievement.description}>
      <span className="ppp-ach-icon">{achievement.icon || '🏆'}</span>
      <span className="ppp-ach-name">{achievement.display_name}</span>
    </div>
  );
}

function SkillBar({ skill, value }) {
  const pct = Math.min(100, Math.round(value));
  return (
    <div className="ppp-skill-bar">
      <div className="ppp-skill-info">
        <span className="ppp-skill-name">{skill.replace(/_/g, ' ')}</span>
        <span className="ppp-skill-val">{Math.round(value)}</span>
      </div>
      <div className="ppp-bar-track">
        <div className="ppp-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CharacterCard({ character, archetypeDescription, onChangePath }) {
  const [expanded, setExpanded] = useState(false);
  const skills = character.skill_tree || {};
  const hasSkills = Object.keys(skills).length > 0;

  return (
    <div className="ppp-char-card">
      <div className="ppp-char-header" onClick={() => setExpanded((v) => !v)}>
        <div className="ppp-char-left">
          <span className="ppp-char-icon">🎮</span>
          <div>
            <div className="ppp-char-name">{character.display_name}</div>
            <div className="ppp-char-meta">
              {character.character_class} · {character.domain_id}
              {character.active_specialty && ` · ${character.active_specialty}`}
            </div>
            {archetypeDescription && (
              <div className="ppp-char-archetype-desc">{archetypeDescription}</div>
            )}
          </div>
        </div>
        <div className="ppp-char-header-right">
          {onChangePath && (
            <button
              className="ppp-change-path-btn"
              onClick={(e) => { e.stopPropagation(); onChangePath(character); }}
              title="Switch your learning path for this domain"
            >
              Change Path
            </button>
          )}
          <span className="ppp-expand-icon">{expanded ? '▾' : '▸'}</span>
        </div>
      </div>
      {expanded && (
        <div className="ppp-char-skills">
          {!hasSkills ? (
            <div className="ppp-training-progress">
              <div className="ppp-training-ring">
                <svg width="40" height="40" viewBox="0 0 40 40">
                  <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="4" />
                  <circle cx="20" cy="20" r="16" fill="none" stroke="#92140C" strokeWidth="4"
                    strokeDasharray="25 76" strokeLinecap="round" transform="rotate(-90 20 20)" />
                </svg>
              </div>
              <div>
                <div className="ppp-training-label">Training in progress...</div>
                <div className="ppp-training-sub">Complete modules to build your skill tree</div>
              </div>
            </div>
          ) : (
            Object.entries(skills).map(([skill, val]) => (
              <SkillBar key={skill} skill={skill} value={typeof val === 'number' ? val : 0} />
            ))
          )}
          {character.active_specialty && character.specialty_skill_trees?.[character.active_specialty] && (
            <>
              <div className="ppp-specialty-label">{character.active_specialty} Specialty Skills</div>
              {Object.entries(character.specialty_skill_trees[character.active_specialty]).map(([s, v]) => (
                <SkillBar key={s} skill={s} value={typeof v === 'number' ? v : 0} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DomainXpCard({ domain, xpTotal, rank, level, levelTitle, xpToNext, xpProgressPct }) {
  const domainLabels = {
    ai: 'AI',
    ai_orchestrator: 'AI Orchestrator',
    ai_beginner: 'AI Beginner',
    ai_entrepreneur: 'AI Entrepreneur',
  };
  const displayLevel = level || Math.max(1, 1);
  const pct = typeof xpProgressPct === 'number' ? Math.min(100, Math.max(0, xpProgressPct)) : 0;
  const toNext = typeof xpToNext === 'number' ? xpToNext : 0;

  return (
    <div className="ppp-domain-xp-card">
      <div className="ppp-domain-xp-header">
        <span className="ppp-domain-name">{domainLabels[domain] || domain}</span>
        {rank && <span className="ppp-domain-rank">#{rank}</span>}
      </div>
      <div className="ppp-domain-xp-row">
        <span className="ppp-domain-level">
          Level {displayLevel}{levelTitle ? ` · ${levelTitle}` : ''}
        </span>
        <span className="ppp-domain-xp-val">{(xpTotal || 0).toLocaleString()} XP</span>
      </div>
      <div className="ppp-xp-bar-track">
        <div className="ppp-xp-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="ppp-xp-next">
        {toNext > 0
          ? `${Math.round(toNext)} XP to next level`
          : (displayLevel <= 1 || !(xpTotal > 0))
            ? 'Complete lessons to earn XP'
            : (pct >= 99 ? 'Max level reached' : 'Keep learning to level up')}
      </div>
    </div>
  );
}

// ─── Profile Tab ───────────────────────────────────────────────────────────

const DOMAIN_LABELS = {
  ai_orchestrator: 'AI Orchestrator',
  ai_beginner: 'AI Beginner',
  ai_entrepreneur: 'AI Entrepreneur',
  football: 'Football IQ',
  lsat: 'LSAT Prep',
  sat: 'SAT Prep',
};

function ProfileTab({ learner, participant, characters, domainXp }) {
  const navigate = useNavigate();
  const enrolledDomains = learner?.enrolled_domains || [];

  // Flashcard review queue state
  const [reviewQueue, setReviewQueue] = useState(null); // { total: N, by_domain: { [domain]: N } }
  const [reviewQueueLoading, setReviewQueueLoading] = useState(false);

  useEffect(() => {
    const learnerId = learner?.learner_id;
    if (!learnerId) return;
    setReviewQueueLoading(true);
    adaptiveAPI.getReviewQueue(learnerId)
      .then(({ data }) => {
        // Normalise: accept { total, by_domain } or an array of cards
        if (Array.isArray(data)) {
          const byDomain = {};
          data.forEach((c) => {
            const d = c.domain_id || c.domain || 'unknown';
            byDomain[d] = (byDomain[d] || 0) + 1;
          });
          setReviewQueue({ total: data.length, by_domain: byDomain });
        } else {
          setReviewQueue({ total: data.total || 0, by_domain: data.by_domain || {} });
        }
      })
      .catch(() => setReviewQueue({ total: 0, by_domain: {} }))
      .finally(() => setReviewQueueLoading(false));
  }, [learner?.learner_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Character archetype state — fetched per domain for description + "Change Path"
  const [archetypesByDomain, setArchetypesByDomain] = useState({}); // { [domain]: { enabled, classes, displayName } }
  const [changePathModal, setChangePathModal] = useState(null); // { character, domain, classes, displayName }

  useEffect(() => {
    // Fetch character class info for each enrolled domain that has archetypes
    enrolledDomains.forEach((domain) => {
      characterAPI.getCharacterClasses(domain)
        .then(({ data }) => {
          if (data?.character_archetypes_enabled) {
            setArchetypesByDomain((prev) => ({
              ...prev,
              [domain]: {
                enabled: true,
                classes: data.character_classes || [],
                displayName: data.character_display_name || 'Learning Path',
              },
            }));
          }
        })
        .catch(() => { /* non-fatal */ });
    });
  }, [enrolledDomains.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePathSelect = async (classId) => {
    if (!changePathModal) return;
    try {
      await characterAPI.selectCharacter(changePathModal.domain, classId);
    } catch { /* non-fatal */ }
    setChangePathModal(null);
  };

  const handleChangePathSkip = (defaultClassId) => {
    setChangePathModal(null);
  };

  // Resolve archetype description for a character
  const getArchetypeDescription = (character) => {
    const domainData = archetypesByDomain[character.domain_id];
    if (!domainData?.enabled) return null;
    const cls = domainData.classes.find(
      (c) => (c.id || c.class_id) === character.character_class ||
              (c.label || c.display_name) === character.character_class
    );
    return cls?.description || null;
  };

  // Prefer the primary enrolled domain profile (API uses real level curve).
  const primaryDomain = enrolledDomains[0];
  const primary = (primaryDomain && domainXp[primaryDomain]) || {};
  const totalXp = Object.values(domainXp).reduce((sum, v) => sum + (v?.xp_total || 0), 0)
    || (primary.xp_total || 0);
  const globalLevel = primary.level || 1;
  const levelPct = typeof primary.xp_progress_pct === 'number' ? primary.xp_progress_pct : 0;
  const xpToNext = typeof primary.xp_to_next_level === 'number' ? primary.xp_to_next_level : 0;

  return (
    <>
      {/* Global Stats */}
      <div className="ppp-global-card">
        <div className="ppp-avatar">
          {learner?.display_name?.[0]?.toUpperCase() || 'P'}
        </div>
        <div className="ppp-global-info">
          <div className="ppp-display-name">{learner?.display_name || learner?.email}</div>
          <div className="ppp-level-row">
            <span className="ppp-level">
              Level {globalLevel}{primary.level_title ? ` · ${primary.level_title}` : ''}
            </span>
            <span className="ppp-xp">{totalXp.toLocaleString()} XP total</span>
          </div>
          <div className="ppp-xp-bar-track">
            <div className="ppp-xp-bar-fill" style={{ width: `${levelPct}%` }} />
          </div>
          <div className="ppp-xp-next">
            {xpToNext > 0
              ? `${Math.round(xpToNext)} XP to next level`
              : (globalLevel <= 1 || !(totalXp > 0))
                ? 'Complete lessons to earn XP'
                : (levelPct >= 99 ? 'Max level reached' : 'Keep learning to level up')}
          </div>
        </div>
      </div>

      {/* Flashcard Review */}
      {enrolledDomains.length > 0 && (
        <div className="ppp-section">
          <h3>Flashcard Review</h3>
          <div className="ppp-flashcard-review-card" style={{
            background: 'rgba(255,207,153,0.1)',
            border: '1px solid #FFCF99',
            borderRadius: 10,
            padding: '14px 18px',
          }}>
            {reviewQueueLoading ? (
              <p style={{ color: '#6a6a72', fontSize: 13, margin: 0 }}>Loading review queue…</p>
            ) : reviewQueue && reviewQueue.total > 0 ? (
              <>
                <p style={{ color: '#1E1E24', fontSize: 13, marginBottom: 12, marginTop: 0 }}>
                  <strong style={{ color: '#92140C' }}>{reviewQueue.total}</strong> card{reviewQueue.total !== 1 ? 's' : ''} due for review
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {enrolledDomains.map((domain) => {
                    const count = (reviewQueue.by_domain || {})[domain] || 0;
                    return (
                      <button
                        key={domain}
                        className="ppp-domain-xp-card"
                        onClick={() => navigate(`/flashcards/${domain}`)}
                        style={{
                          cursor: 'pointer',
                          textAlign: 'left',
                          background: count > 0 ? 'rgba(146,20,12,0.08)' : '#f9fafb',
                          border: count > 0 ? '1px solid #92140C' : '1px solid #FFCF99',
                          borderRadius: 8,
                          padding: '10px 16px',
                          color: count > 0 ? '#92140C' : '#6a6a72',
                          fontSize: 13,
                          fontWeight: 500,
                          transition: 'background 0.15s',
                        }}
                      >
                        Review {DOMAIN_LABELS[domain] || domain.replace(/_/g, ' ')} Cards
                        {count > 0 && (
                          <span style={{
                            marginLeft: 8,
                            background: '#92140C',
                            color: '#fff',
                            borderRadius: 12,
                            padding: '2px 8px',
                            fontSize: 11,
                            fontWeight: 700,
                          }}>
                            {count} due
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <p style={{ color: '#6a6a72', fontSize: 13, margin: 0 }}>
                No cards due for review — check back later!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Per-domain XP cards */}
      {enrolledDomains.length > 0 && (
        <div className="ppp-section">
          <h3>Domain Progress</h3>
          <div className="ppp-domain-xp-grid">
            {enrolledDomains.map((domain) => (
              <DomainXpCard
                key={domain}
                domain={domain}
                xpTotal={domainXp[domain]?.xp_total || 0}
                rank={domainXp[domain]?.rank}
                level={domainXp[domain]?.level}
                levelTitle={domainXp[domain]?.level_title}
                xpToNext={domainXp[domain]?.xp_to_next_level}
                xpProgressPct={domainXp[domain]?.xp_progress_pct}
              />
            ))}
          </div>
        </div>
      )}

      {/* Achievements */}
      {participant?.achievements?.length > 0 && (
        <div className="ppp-section">
          <h3>Achievements ({participant.achievements.length})</h3>
          <div className="ppp-achievements-grid">
            {participant.achievements.map((a, i) => (
              <AchievementBadge key={i} achievement={a} />
            ))}
          </div>
        </div>
      )}

      {/* Characters */}
      <div className="ppp-section">
        <h3>Characters ({characters.length})</h3>
        {characters.length === 0 ? (
          <p className="ppp-empty">No characters yet. Enroll in a domain to create one.</p>
        ) : (
          <div className="ppp-chars-list">
            {characters.map((c) => {
              const domainData = archetypesByDomain[c.domain_id];
              return (
                <CharacterCard
                  key={c.character_id}
                  character={c}
                  archetypeDescription={getArchetypeDescription(c)}
                  onChangePath={domainData?.enabled ? (char) => {
                    setChangePathModal({
                      character: char,
                      domain: char.domain_id,
                      classes: domainData.classes,
                      displayName: domainData.displayName,
                    });
                  } : null}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Change Path modal */}
      {changePathModal && (
        <CharacterPickerModal
          domain={changePathModal.domain}
          characterClasses={changePathModal.classes}
          characterDisplayName={changePathModal.displayName}
          onSelect={handleChangePathSelect}
          onSkip={handleChangePathSkip}
        />
      )}
    </>
  );
}

// ─── Settings Tab ──────────────────────────────────────────────────────────

const REMINDER_OPTIONS = [
  { value: 'morning', label: 'Morning (8am)', desc: 'Get reminded each morning when flashcards are due' },
  { value: 'evening', label: 'Evening (6pm)', desc: 'Get reminded each evening when flashcards are due' },
  { value: 'both',    label: 'Both',          desc: 'Morning and evening reminders' },
  { value: 'off',     label: 'Off',            desc: 'No flashcard review reminders' },
];

function SavedBadge({ show }) {
  if (!show) return null;
  return <span className="ppp-saved-badge">Saved ✓</span>;
}

function SettingsTab({ learner }) {
  const [displayName, setDisplayName] = useState(learner?.display_name || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState('');

  const [reviewReminder, setReviewReminder] = useState(null); // loaded from profile
  const [reminderSaved, setReminderSaved] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);

  const [emailPrefs, setEmailPrefs] = useState({
    inactivity_reminders: true,
    milestone_notifications: true,
    weekly_digest: true,
  });
  const [emailPrefsLoaded, setEmailPrefsLoaded] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);

  // Load current profile (for review_reminder) and email prefs
  useEffect(() => {
    authAPI.getProfile().then(({ data }) => {
      setReviewReminder(data.review_reminder || 'off');
    }).catch(() => {
      setReviewReminder('off');
    });

    emailAPI.getPrefs().then(({ data }) => {
      setEmailPrefs({
        inactivity_reminders: data.inactivity_reminders !== false,
        milestone_notifications: data.milestone_notifications !== false,
        weekly_digest: data.weekly_digest !== false,
      });
      setEmailPrefsLoaded(true);
    }).catch(() => {
      setEmailPrefsLoaded(true);
    });
  }, []);

  const handleSaveName = async () => {
    setNameLoading(true);
    setNameError('');
    try {
      await authAPI.updateProfile({ display_name: displayName.trim() });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 3000);
    } catch (e) {
      setNameError('Failed to save. Please try again.');
    } finally {
      setNameLoading(false);
    }
  };

  const handleReminderChange = async (value) => {
    setReviewReminder(value);
    setReminderLoading(true);
    try {
      await authAPI.updateProfile({ review_reminder: value });
      setReminderSaved(true);
      setTimeout(() => setReminderSaved(false), 3000);
    } catch (e) {
      // silently revert
    } finally {
      setReminderLoading(false);
    }
  };

  const handleEmailToggle = async (key) => {
    const updated = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(updated);
    setEmailSaved(false);
    try {
      await emailAPI.updatePrefs(updated);
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 3000);
    } catch (e) {
      // revert on error
      setEmailPrefs(emailPrefs);
    }
  };

  return (
    <div className="ppp-settings-container">
      {/* Section 1: Display Name */}
      <div className="ppp-settings-section">
        <div className="ppp-settings-section-header">
          <span className="ppp-settings-section-icon">👤</span>
          <div>
            <div className="ppp-settings-section-title">Profile</div>
            <div className="ppp-settings-section-desc">Update your display name</div>
          </div>
        </div>
        <div className="ppp-settings-field">
          <label className="ppp-settings-label">Display Name</label>
          <div className="ppp-settings-input-row">
            <input
              className="ppp-settings-input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter display name"
            />
            <button
              className="ppp-settings-save-btn"
              onClick={handleSaveName}
              disabled={nameLoading || !displayName.trim()}
            >
              {nameLoading ? 'Saving...' : 'Save'}
            </button>
            <SavedBadge show={nameSaved} />
          </div>
          {nameError && <div className="ppp-settings-error">{nameError}</div>}
        </div>
      </div>

      {/* Section 2: Flashcard Review Reminders */}
      <div className="ppp-settings-section">
        <div className="ppp-settings-section-header">
          <span className="ppp-settings-section-icon">🃏</span>
          <div>
            <div className="ppp-settings-section-title">Flashcard Review Reminders</div>
            <div className="ppp-settings-section-desc">
              Get notified when flashcards are due for review.
            </div>
          </div>
          {reminderLoading && <span className="ppp-settings-spinner">⟳</span>}
          <SavedBadge show={reminderSaved} />
        </div>
        <div className="ppp-reminder-options">
          {REMINDER_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`ppp-reminder-option${reviewReminder === opt.value ? ' ppp-reminder-selected' : ''}`}
            >
              <input
                type="radio"
                name="review_reminder"
                value={opt.value}
                checked={reviewReminder === opt.value}
                onChange={() => handleReminderChange(opt.value)}
                className="ppp-reminder-radio"
              />
              <div className="ppp-reminder-text">
                <span className="ppp-reminder-label">{opt.label}</span>
                <span className="ppp-reminder-desc">{opt.desc}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Section 3: Email Notifications */}
      <div className="ppp-settings-section">
        <div className="ppp-settings-section-header">
          <span className="ppp-settings-section-icon">✉️</span>
          <div>
            <div className="ppp-settings-section-title">Email Notifications</div>
            <div className="ppp-settings-section-desc">
              Manage which emails you receive from TeachIQ.
            </div>
          </div>
          <SavedBadge show={emailSaved} />
        </div>
        {!emailPrefsLoaded ? (
          <div className="ppp-settings-loading">Loading preferences...</div>
        ) : (
          <div className="ppp-email-toggles">
            {[
              { key: 'inactivity_reminders',    label: 'Inactivity Reminders',    desc: "We'll nudge you when you haven't practiced in a while" },
              { key: 'milestone_notifications',  label: 'Milestone Notifications', desc: 'Celebrate your achievements via email' },
              { key: 'weekly_digest',            label: 'Weekly Digest',           desc: 'A weekly summary of your progress and upcoming reviews' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="ppp-email-toggle-row">
                <div className="ppp-email-toggle-text">
                  <span className="ppp-email-toggle-label">{label}</span>
                  <span className="ppp-email-toggle-desc">{desc}</span>
                </div>
                <button
                  className={`ppp-toggle-btn${emailPrefs[key] ? ' ppp-toggle-on' : ''}`}
                  onClick={() => handleEmailToggle(key)}
                  aria-checked={emailPrefs[key]}
                  role="switch"
                >
                  <span className="ppp-toggle-thumb" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function PlayerProfilePage() {
  const { learner } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [participant, setParticipant] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [domainXp, setDomainXp] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!learner?.learner_id) return;

    const enrolledDomains = learner?.enrolled_domains || [];

    Promise.all([
      // Participant (for achievements + characters)
      participantAPI.getParticipant(learner.learner_id)
        .then(({ data }) => {
          setParticipant(data);
          // Use embedded character objects if present; fall back to domain_characters values
          const allChars = Array.isArray(data.characters) && data.characters.length > 0
            ? data.characters
            : Object.values(data.domain_characters || {})
                .filter((v) => v && typeof v === 'object');  // filter out plain char_id strings
          setCharacters(allChars);
        })
        .catch(() => {}),

      // XP from gamification profile (real level curve) + leaderboard rank
      ...enrolledDomains.map((domain) =>
        Promise.all([
          gamificationAPI.getProfile(domain).then(({ data }) => data).catch(() => null),
          gamificationAPI.getLeaderboard({ scope: 'domain', domain })
            .then(({ data }) => data)
            .catch(() => null),
        ]).then(([profile, board]) => {
          const myEntry = board?.learner_rank || board?.entries?.find(
            (e) => e.learner_id === learner.learner_id
          );
          setDomainXp((prev) => ({
            ...prev,
            [domain]: {
              xp_total: profile?.xp_total ?? myEntry?.xp_total ?? 0,
              level: profile?.level ?? myEntry?.level ?? 1,
              level_title: profile?.level_title || '',
              xp_to_next_level: profile?.xp_to_next_level ?? 0,
              xp_progress_pct: profile?.xp_progress_pct ?? 0,
              rank: myEntry?.rank,
            },
          }));
        }).catch(() => {
          setDomainXp((prev) => ({ ...prev, [domain]: { xp_total: 0, level: 1 } }));
        })
      ),
    ])
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [learner]);

  if (loading) return <div className="ppp-loading">Loading profile...</div>;
  if (error) return <div className="ppp-error">Error: {error}</div>;

  return (
    <div className="ppp-page">
      <LearnerNav />
      <div className="ppp-header">
        <h2>Player Profile</h2>
      </div>

      {/* Tab Nav */}
      <div className="ppp-tabs">
        <button
          className={`ppp-tab${activeTab === 'profile' ? ' ppp-tab-active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={`ppp-tab${activeTab === 'settings' ? ' ppp-tab-active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {activeTab === 'profile' ? (
        <ProfileTab
          learner={learner}
          participant={participant}
          characters={characters}
          domainXp={domainXp}
        />
      ) : (
        <SettingsTab learner={learner} />
      )}

      <style>{`
        /* ── Base ── */
        .ppp-page { min-height: 100vh; background: #FFF8F0; color: #1E1E24; padding: 2rem; font-family: system-ui, sans-serif; max-width: 800px; margin: 0 auto; }
        .ppp-loading, .ppp-error { display: flex; justify-content: center; align-items: center; min-height: 100vh; color: #6a6a72; }

        /* ── Header ── */
        .ppp-header { display: flex; align-items: center; gap: 1.5rem; margin-bottom: 1.5rem; }
        .ppp-back-btn { background: #FFF8F0; border: 1px solid #FFCF99; color: #92140C; padding: 0.4rem 0.8rem; border-radius: 0.5rem; cursor: pointer; font-weight: 500; transition: background 0.15s; }
        .ppp-back-btn:hover { background: rgba(255,207,153,0.2); }
        .ppp-header h2 { margin: 0; font-size: 1.5rem; color: #1E1E24; }

        /* ── Tabs ── */
        .ppp-tabs { display: flex; gap: 0.5rem; margin-bottom: 2rem; border-bottom: 1px solid #FFCF99; }
        .ppp-tab { background: none; border: none; color: #6a6a72; padding: 0.6rem 1.2rem; cursor: pointer; font-size: 0.95rem; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color 0.15s, border-color 0.15s; }
        .ppp-tab:hover { color: #1E1E24; }
        .ppp-tab-active { color: #92140C; border-bottom-color: #92140C; font-weight: 600; }

        /* ── Global card ── */
        .ppp-global-card { display: flex; gap: 1.5rem; align-items: flex-start; background: #fff; border: 1px solid #FFCF99; border-radius: 1rem; padding: 1.5rem; margin-bottom: 2rem; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
        .ppp-avatar { width: 60px; height: 60px; border-radius: 50%; background: linear-gradient(135deg, #92140C, #b21a10); display: flex; align-items: center; justify-content: center; font-size: 1.75rem; font-weight: 700; color: #fff; flex-shrink: 0; }
        .ppp-global-info { flex: 1; display: flex; flex-direction: column; gap: 0.4rem; }
        .ppp-display-name { font-size: 1.25rem; font-weight: 700; color: #1E1E24; }
        .ppp-level-row { display: flex; gap: 1rem; align-items: center; }
        .ppp-level { color: #92140C; font-weight: 700; font-size: 0.95rem; }
        .ppp-xp { color: #1E1E24; font-size: 0.85rem; font-weight: 500; }
        .ppp-xp-bar-track { height: 6px; background: rgba(146,20,12,0.12); border-radius: 9999px; overflow: hidden; }
        .ppp-xp-bar-fill { height: 100%; background: linear-gradient(90deg, #92140C, #b21a10); border-radius: 9999px; transition: width 0.3s; }
        .ppp-xp-next { font-size: 0.75rem; color: #6B6558; }

        /* ── Domain XP grid ── */
        .ppp-domain-xp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; }
        .ppp-domain-xp-card { background: #fff; border: 1px solid #FFCF99; border-radius: 0.75rem; padding: 1rem; display: flex; flex-direction: column; gap: 0.4rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .ppp-domain-xp-header { display: flex; justify-content: space-between; align-items: center; }
        .ppp-domain-name { font-size: 0.85rem; font-weight: 600; color: #92140C; }
        .ppp-domain-rank { font-size: 0.75rem; color: #6a6a72; }
        .ppp-domain-xp-row { display: flex; justify-content: space-between; align-items: center; }
        .ppp-domain-level { font-size: 0.8rem; color: #1E1E24; font-weight: 500; }
        .ppp-domain-xp-val { font-size: 0.8rem; color: #92140C; font-weight: 600; }

        /* ── Sections ── */
        .ppp-section { margin-bottom: 2rem; }
        .ppp-section h3 { font-size: 1.1rem; margin-bottom: 1rem; color: #1E1E24; }
        .ppp-achievements-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; }
        .ppp-achievement { display: flex; flex-direction: column; align-items: center; gap: 0.25rem; background: #fff; border: 1px solid #FFCF99; border-radius: 0.75rem; padding: 0.75rem 1rem; min-width: 80px; }
        .ppp-ach-icon { font-size: 1.5rem; }
        .ppp-ach-name { font-size: 0.7rem; text-align: center; color: #6a6a72; }
        .ppp-empty { color: #6a6a72; font-size: 0.9rem; }

        /* ── Characters ── */
        .ppp-chars-list { display: flex; flex-direction: column; gap: 0.75rem; }
        .ppp-char-card { background: #fff; border: 1px solid #FFCF99; border-radius: 0.75rem; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .ppp-char-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; cursor: pointer; }
        .ppp-char-header:hover { background: rgba(255,207,153,0.12); }
        .ppp-char-left { display: flex; gap: 0.75rem; align-items: center; flex: 1; min-width: 0; }
        .ppp-char-icon { font-size: 1.5rem; flex-shrink: 0; }
        .ppp-char-name { font-weight: 600; margin-bottom: 0.1rem; color: #1E1E24; }
        .ppp-char-meta { font-size: 0.75rem; color: #6a6a72; }
        .ppp-char-archetype-desc { font-size: 0.75rem; color: #6a6a72; margin-top: 0.2rem; font-style: italic; }
        .ppp-char-header-right { display: flex; align-items: center; gap: 0.6rem; flex-shrink: 0; }
        .ppp-expand-icon { color: #92140C; }
        .ppp-change-path-btn {
          font-size: 0.72rem; font-weight: 600; padding: 0.25rem 0.6rem;
          background: none; border: 1px solid #92140C;
          color: #92140C; border-radius: 0.4rem; cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
          white-space: nowrap;
        }
        .ppp-change-path-btn:hover { background: rgba(146,20,12,0.08); }
        .ppp-char-skills { padding: 0 1.25rem 1rem; display: flex; flex-direction: column; gap: 0.5rem; }
        .ppp-specialty-label { font-size: 0.75rem; color: #92140C; font-weight: 600; margin-top: 0.5rem; }
        .ppp-skill-bar { display: flex; flex-direction: column; gap: 0.2rem; }
        .ppp-skill-info { display: flex; justify-content: space-between; font-size: 0.8rem; }
        .ppp-skill-name { color: #1E1E24; text-transform: capitalize; }
        .ppp-skill-val { color: #92140C; font-weight: 600; }
        .ppp-bar-track { height: 4px; background: rgba(146,20,12,0.12); border-radius: 9999px; overflow: hidden; }
        .ppp-bar-fill { height: 100%; background: linear-gradient(90deg, #92140C, #b21a10); border-radius: 9999px; }

        /* ── Training-in-progress ── */
        .ppp-training-progress { display: flex; align-items: center; gap: 0.75rem; padding: 0.5rem 0; }
        .ppp-training-ring { flex-shrink: 0; }
        .ppp-training-label { font-size: 0.875rem; color: #92140C; font-weight: 600; }
        .ppp-training-sub { font-size: 0.75rem; color: #6a6a72; margin-top: 0.15rem; }

        /* ── Settings ── */
        .ppp-settings-container { display: flex; flex-direction: column; gap: 1.5rem; }
        .ppp-settings-section { background: #fff; border: 1px solid #FFCF99; border-radius: 1rem; padding: 1.5rem; display: flex; flex-direction: column; gap: 1.25rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .ppp-settings-section-header { display: flex; align-items: flex-start; gap: 0.75rem; }
        .ppp-settings-section-icon { font-size: 1.25rem; flex-shrink: 0; margin-top: 0.1rem; }
        .ppp-settings-section-title { font-weight: 700; font-size: 1rem; margin-bottom: 0.2rem; color: #1E1E24; }
        .ppp-settings-section-desc { font-size: 0.8rem; color: #6a6a72; }
        .ppp-settings-spinner { margin-left: auto; color: #92140C; animation: ppp-spin 1s linear infinite; }
        @keyframes ppp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .ppp-saved-badge { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 0.78rem; color: #22c55e; font-weight: 600; margin-left: auto; animation: ppp-fade-in 0.2s ease; }
        @keyframes ppp-fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .ppp-settings-field { display: flex; flex-direction: column; gap: 0.5rem; }
        .ppp-settings-label { font-size: 0.8rem; font-weight: 600; color: #6a6a72; text-transform: uppercase; letter-spacing: 0.05em; }
        .ppp-settings-input-row { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }
        .ppp-settings-input { flex: 1; min-width: 160px; background: #fff; border: 1px solid #FFCF99; border-radius: 0.5rem; padding: 0.5rem 0.75rem; color: #1E1E24; font-size: 0.9rem; outline: none; }
        .ppp-settings-input:focus { border-color: #92140C; }
        .ppp-settings-save-btn { background: #92140C; color: #fff; border: none; border-radius: 0.5rem; padding: 0.5rem 1rem; font-size: 0.875rem; font-weight: 600; cursor: pointer; transition: background 0.15s; }
        .ppp-settings-save-btn:hover:not(:disabled) { background: #7a100a; }
        .ppp-settings-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ppp-settings-error { font-size: 0.8rem; color: #ef4444; }
        .ppp-settings-loading { color: #6a6a72; font-size: 0.875rem; }

        /* ── Reminder radio options ── */
        .ppp-reminder-options { display: flex; flex-direction: column; gap: 0.5rem; }
        .ppp-reminder-option { display: flex; align-items: flex-start; gap: 0.75rem; padding: 0.75rem 1rem; border: 1px solid #FFCF99; border-radius: 0.625rem; cursor: pointer; transition: border-color 0.15s, background 0.15s; }
        .ppp-reminder-option:hover { background: rgba(255,207,153,0.12); border-color: #FFCF99; }
        .ppp-reminder-selected { border-color: #92140C !important; background: rgba(146,20,12,0.06) !important; }
        .ppp-reminder-radio { accent-color: #92140C; margin-top: 0.15rem; flex-shrink: 0; width: 16px; height: 16px; cursor: pointer; }
        .ppp-reminder-text { display: flex; flex-direction: column; gap: 0.15rem; }
        .ppp-reminder-label { font-size: 0.875rem; font-weight: 600; color: #1E1E24; }
        .ppp-reminder-desc { font-size: 0.75rem; color: #6a6a72; }

        /* ── Email toggles ── */
        .ppp-email-toggles { display: flex; flex-direction: column; gap: 0.75rem; }
        .ppp-email-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 0.75rem 0; border-bottom: 1px solid rgba(0,0,0,0.06); }
        .ppp-email-toggle-row:last-child { border-bottom: none; padding-bottom: 0; }
        .ppp-email-toggle-text { display: flex; flex-direction: column; gap: 0.15rem; }
        .ppp-email-toggle-label { font-size: 0.875rem; font-weight: 600; color: #1E1E24; }
        .ppp-email-toggle-desc { font-size: 0.75rem; color: #6a6a72; }
        .ppp-toggle-btn { position: relative; width: 44px; height: 24px; border-radius: 9999px; background: #d1d5db; border: none; cursor: pointer; transition: background 0.2s; flex-shrink: 0; }
        .ppp-toggle-on { background: #92140C; }
        .ppp-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform 0.2s; display: block; }
        .ppp-toggle-on .ppp-toggle-thumb { transform: translateX(20px); }
      `}</style>
    </div>
  );
}
